import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import styles from "./FeeSummaryTrendCard.module.css";

/* Map card `variant` → CSS class + line color.
 * Add a new key here + a matching CSS block to introduce a new variant. */
const VARIANT = {
  blue: { cardClass: styles.cardBlue, stroke: "#2f8fe0" },
  purple: { cardClass: styles.cardPurple, stroke: "#29b6d8" },
};

/* Custom tooltip — dark pill showing the point's main value,
 * plus an optional secondary line (e.g. "Students Count : 380"). */
const CustomTooltip = ({ active, payload, label, title, secondaryLabel }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;

  return (
    <div className={styles.tooltip}>
      <div>
        {title} : <strong>{point.displayValue ?? point.value}</strong>
      </div>
      {secondaryLabel && point.secondaryValue !== undefined && (
        <div>
          {secondaryLabel} : <strong>{point.secondaryValue}</strong>
        </div>
      )}
    </div>
  );
};

/* Formatter for clean Y-axis ticks (17000 -> "17K", 125000 -> "1.25L") */
const defaultYAxisTickFormatter = (value) => {
  const num = Number(value);
  if (isNaN(num) || num === 0) return "0";
  if (num >= 10000000)
    return `${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)}Cr`;
  if (num >= 100000)
    return `${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}K`;
  return num.toString();
};

const FeeSummaryTrendCard = ({
  variant = "blue",
  title,
  amount,
  subtitle,
  secondaryLabel,
  data = [],
  loading = false,
  error = null,
  emptyMessage = "No data available",
  yTickFormatter,
}) => {
  const { cardClass, stroke } = VARIANT[variant] || VARIANT.blue;

  const renderChart = () => {
    if (loading) return <div className={styles.stateBox}>Loading…</div>;
    if (error)
      return (
        <div className={`${styles.stateBox} ${styles.stateError}`}>
          {String(error)}
        </div>
      );
    if (!Array.isArray(data) || data.length === 0)
      return <div className={styles.stateBox}>{emptyMessage}</div>;

    return (
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 10, right: 15, left: -5, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.06)" />
          <XAxis
            dataKey="year"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "var(--color-text)" }}
          />
          <YAxis
            domain={[0, "auto"]}
            allowDataOverflow={false}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "var(--color-text)" }}
            tickFormatter={yTickFormatter || defaultYAxisTickFormatter}
            width={55}
          />
          <Tooltip
            content={<CustomTooltip title={title} secondaryLabel={secondaryLabel} />}
            cursor={{ stroke: "rgba(0,0,0,0.1)" }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={2.5}
            dot={{ r: 4, fill: stroke, strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className={`${styles.card} ${cardClass}`}>
      <div className={styles.cardTop}>
        <span className={styles.amount}>{amount}</span>
        <span className={styles.title}>{title}</span>
      </div>

      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}

      <div className={styles.chartWrap}>{renderChart()}</div>
    </div>
  );
};

export default FeeSummaryTrendCard;
